import React, { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import OrderDetailsModal from './OrderDetailsModal';
import JakeOrderModal from './JakeOrderModal';
import SpikesGearHeader from './SpikesGearHeader';
import './Dashboard.css';

const STATUS_COLORS = {
  processing: '#f59e0b',
  completed: '#8b5cf6',
  'picked up': '#22c55e',
};

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/spikes-gear/orders', {
        params: { limit: 1000 },
      });
      setOrders(response.data.orders || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getShirtCount = (order) =>
    (order.items || []).reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0);

  const getOrderTotal = (order) => getShirtCount(order) * parseFloat(order.price || 0);

  const filteredOrders = orders.filter((order) => {
    const matchesStatus = filterStatus === 'all' || order.order_status === filterStatus;

    if (!searchTerm.trim()) {
      return matchesStatus;
    }

    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (order.customer_name && order.customer_name.toLowerCase().includes(searchLower)) ||
      (order.customer_phone && order.customer_phone.toLowerCase().includes(searchLower)) ||
      (order.items || []).some((i) => i.name_on_back?.toLowerCase().includes(searchLower));

    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  const handleCreateOrder = () => {
    setIsCreateOrderModalOpen(true);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderSuccess = () => {
    fetchOrders();
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.patch(`/spikes-gear/orders/${orderId}/status`, {
        order_status: newStatus,
      });
      setOrders((prev) =>
        prev.map((order) => (order.id === orderId ? { ...order, order_status: newStatus } : order))
      );
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status');
    }
  };

  const toggleSelect = (orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const pageIds = paginatedOrders.map((o) => o.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllFiltered = () => setSelectedIds(new Set(filteredOrders.map((o) => o.id)));

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      const orderIds = Array.from(selectedIds);
      await api.patch('/spikes-gear/orders/bulk-status', {
        order_ids: orderIds,
        order_status: newStatus,
      });
      setOrders((prev) =>
        prev.map((order) =>
          selectedIds.has(order.id) ? { ...order, order_status: newStatus } : order
        )
      );
      clearSelection();
    } catch (err) {
      console.error('Failed to bulk update order status:', err);
      alert('Failed to update selected orders');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Delete ${count} order${count === 1 ? '' : 's'}? This action cannot be undone.`)) {
      return;
    }
    setBulkUpdating(true);
    try {
      const orderIds = Array.from(selectedIds);
      await api.delete('/spikes-gear/orders/bulk-delete', { data: { order_ids: orderIds } });
      setOrders((prev) => prev.filter((order) => !selectedIds.has(order.id)));
      clearSelection();
    } catch (err) {
      console.error('Failed to bulk delete orders:', err);
      alert('Failed to delete selected orders');
    } finally {
      setBulkUpdating(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const StatusSelect = ({ order }) => (
    <select
      className="status-select"
      value={order.order_status}
      onChange={(e) => handleStatusChange(order.id, e.target.value)}
      style={{ borderColor: STATUS_COLORS[order.order_status] || '#ccc' }}
    >
      <option value="processing">Processing</option>
      <option value="completed">Completed</option>
      <option value="picked up">Picked Up</option>
    </select>
  );

  return (
    <div className="spikes-gear-dashboard">
      <SpikesGearHeader />

      <div className="dashboard-header">
        <p>Manage and track Spikes Gear t-shirt orders</p>
      </div>

      {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card">
          <h4>Total Orders</h4>
          <p className="stat-value">{filteredOrders.length}</p>
        </div>
        <div className="stat-card">
          <h4>Total Shirts</h4>
          <p className="stat-value" style={{ color: '#667eea' }}>
            {filteredOrders.reduce((sum, o) => sum + getShirtCount(o), 0)}
          </p>
        </div>
        <div className="stat-card">
          <h4>Total Sales</h4>
          <p className="stat-value" style={{ color: '#667eea' }}>
            $
            {filteredOrders
              .reduce((sum, o) => sum + getOrderTotal(o), 0)
              .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="stat-card">
          <h4>Processing</h4>
          <p className="stat-value" style={{ color: STATUS_COLORS.processing }}>
            {filteredOrders.filter((o) => o.order_status === 'processing').length}
          </p>
        </div>
        <div className="stat-card">
          <h4>Completed</h4>
          <p className="stat-value" style={{ color: STATUS_COLORS.completed }}>
            {filteredOrders.filter((o) => o.order_status === 'completed').length}
          </p>
        </div>
        <div className="stat-card">
          <h4>Picked Up</h4>
          <p className="stat-value" style={{ color: STATUS_COLORS['picked up'] }}>
            {filteredOrders.filter((o) => o.order_status === 'picked up').length}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="controls-top-row">
          <div className="search-filter">
            <input
              type="text"
              placeholder="Search by customer, phone, or name on back..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="picked up">Picked Up</option>
            </select>
          </div>

          <div className="controls-actions">
            {filteredOrders.length > 0 && (
              <button className="btn-select-all-filtered" onClick={selectAllFiltered}>
                Select all {filteredOrders.length} filtered
              </button>
            )}
            <button onClick={handleCreateOrder} className="btn-primary">
              + Create Order
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-action-bar">
          <span className="bulk-action-count">{selectedIds.size} order{selectedIds.size === 1 ? '' : 's'} selected</span>
          <div className="bulk-action-buttons">
            <button
              className="btn-bulk btn-bulk-completed"
              onClick={() => handleBulkStatusChange('completed')}
              disabled={bulkUpdating}
            >
              {bulkUpdating ? 'Updating...' : '✓ Mark Completed'}
            </button>
            <button
              className="btn-bulk btn-bulk-picked-up"
              onClick={() => handleBulkStatusChange('picked up')}
              disabled={bulkUpdating}
            >
              {bulkUpdating ? 'Updating...' : '✓ Mark Picked Up'}
            </button>
            {isAdmin && (
              <button
                className="btn-bulk btn-bulk-delete"
                onClick={handleBulkDelete}
                disabled={bulkUpdating}
              >
                {bulkUpdating ? 'Working...' : '🗑 Delete Selected'}
              </button>
            )}
            <button className="btn-bulk-clear" onClick={clearSelection} disabled={bulkUpdating}>
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : paginatedOrders.length === 0 ? (
        <div className="no-data">
          <p>No orders found. {searchTerm || filterStatus !== 'all' ? 'Try adjusting your filters.' : 'Create your first order!'}</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectPage}
                      title="Select all on this page"
                    />
                  </th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Shirts</th>
                  <th>Salesperson</th>
                  <th>Price/Shirt</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Created Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.id} className={selectedIds.has(order.id) ? 'row-selected' : ''}>
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(order.id)}
                        onChange={() => toggleSelect(order.id)}
                      />
                    </td>
                    <td>{order.customer_name}</td>
                    <td className="phone">{formatPhoneNumber(order.customer_phone)}</td>
                    <td className="qty">{getShirtCount(order)}</td>
                    <td className="salesperson">{order.sales_person || '-'}</td>
                    <td className="price">${parseFloat(order.price || 0).toFixed(2)}</td>
                    <td className="amount">${getOrderTotal(order).toFixed(2)}</td>
                    <td>
                      <StatusSelect order={order} />
                    </td>
                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <button onClick={() => handleViewOrder(order)} className="btn-view">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="orders-mobile-list">
            {paginatedOrders.map((order) => (
              <div key={order.id} className={`order-card ${selectedIds.has(order.id) ? 'order-card-selected' : ''}`}>
                <div className="order-card-header">
                  <div className="order-card-header-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(order.id)}
                      onChange={() => toggleSelect(order.id)}
                    />
                    <span className="order-card-customer-name">{order.customer_name}</span>
                  </div>
                  <StatusSelect order={order} />
                </div>
                <div className="order-card-body">
                  <div className="order-card-row">
                    <span className="order-card-label">Phone</span>
                    <span className="order-card-value">{formatPhoneNumber(order.customer_phone)}</span>
                  </div>
                  <div className="order-card-row">
                    <span className="order-card-label">Shirts</span>
                    <span className="order-card-value">{getShirtCount(order)}</span>
                  </div>
                  <div className="order-card-row">
                    <span className="order-card-label">Total</span>
                    <span className="order-card-value order-card-amount">${getOrderTotal(order).toFixed(2)}</span>
                  </div>
                  <div className="order-card-row">
                    <span className="order-card-label">Salesperson</span>
                    <span className="order-card-value">{order.sales_person || '-'}</span>
                  </div>
                </div>
                <div className="order-card-footer">
                  <span className="order-card-date">{new Date(order.created_at).toLocaleDateString()}</span>
                  <button onClick={() => handleViewOrder(order)} className="btn-view">
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn-pagination"
              >
                ← Previous
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="btn-pagination"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleOrderSuccess}
      />

      {/* Jake Order Modal */}
      <JakeOrderModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        onOrdersCreated={() => {
          fetchOrders();
        }}
      />
    </div>
  );
};

export default Dashboard;
